/**
 * Merko Integrations — React Query bindings.
 *
 * Three behaviours are built in rather than left to call sites, because each
 * one is a correctness rule in the handoff:
 *
 *   • **Gating.** Every query is disabled when `INTEGRATIONS_ENABLED` is false,
 *     so a disabled deployment never fires a request at a route that does not
 *     exist.
 *   • **Nothing retries a 4xx.** A `409`, a stale ID or a validation failure is
 *     not a transient error, and retrying `already_connected` or
 *     `idempotency_conflict` would make a duplicate more likely, not less.
 *   • **Polling stops at a terminal state** and backs off before it does, per
 *     the handoff's 3–5s-then-back-off recommendation.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseQueryOptions,
} from "@tanstack/react-query";
import { fetchAuthSession } from "aws-amplify/auth";
import {
    confirmAction,
    completeAttempt,
    createProfile,
    deleteProfile,
    disconnectApp,
    fetchAction,
    fetchAttempt,
    fetchCatalog,
    fetchJob,
    fetchProfiles,
    fetchSources,
    previewAction,
    previewProfile,
    retryJob,
    startConnect,
    startRun,
    updateProfile,
} from "./api";
import { ACTION_POLL, INTEGRATIONS_ENABLED, JOB_POLL, pollInterval } from "./config";
import { IntegrationClientError, describeFailure, type IntegrationFailure } from "./errors";
import { idempotencyKeyFor, releaseIdempotencyKey } from "./idempotency";
import { clearAttempt, saveAttempt } from "./attemptStore";
import { isTerminalAction } from "./actions";
import {
    lastJobProgress,
    subscribeJobProgress,
    type IntegrationJobProgress,
} from "./jobProgress";
import { isTerminalJob } from "./types";
import type {
    ActionOperation,
    AppId,
    Catalog,
    ExternalAction,
    ImportJob,
    Preview,
    Profile,
    ProfileInput,
    Sources,
} from "./types";

export const integrationsKeys = {
    root: ["integrations-v2"] as const,
    catalog: () => [...integrationsKeys.root, "catalog"] as const,
    sources: (appId: AppId) => [...integrationsKeys.root, "sources", appId] as const,
    attempt: (attemptId: string) => [...integrationsKeys.root, "attempt", attemptId] as const,
    profiles: () => [...integrationsKeys.root, "profiles"] as const,
    job: (jobId: string) => [...integrationsKeys.root, "job", jobId] as const,
    action: (actionId: string) => [...integrationsKeys.root, "action", actionId] as const,
} as const;

/** A 4xx is the server's considered answer, not a blip. */
const retryServerErrorsOnly = (count: number, error: any): boolean => {
    const status = error?.response?.status;
    if (typeof status === "number" && status < 500) return false;
    return count < 2;
};

type Query<T> = Partial<UseQueryOptions<T, any, T, any>>;

// ─── identity, for scoping stored attempts ─────────────────────────────────

/**
 * A stable key for the signed-in Merko user.
 *
 * Used only to scope a pending connection attempt to the person who started
 * it. The Cognito `sub` is an opaque identifier, which is what is wanted here
 * — nothing user-identifying is written to storage beyond it.
 */
export function useMerkoUserKey(): string | null | undefined {
    /* `undefined` while resolving, `null` when there is no signed-in user. The
       callback page needs to tell those apart: one means wait, the other means
       stop. */
    const [userKey, setUserKey] = useState<string | null | undefined>(undefined);

    useEffect(() => {
        let alive = true;
        void resolveMerkoUserKey().then((key) => {
            if (alive) setUserKey(key);
        });
        return () => {
            alive = false;
        };
    }, []);

    return userKey;
}

/** The signed-in user's Cognito `sub`, or null when there is no session. */
export async function resolveMerkoUserKey(): Promise<string | null> {
    try {
        const session = await fetchAuthSession();
        const sub = session.tokens?.idToken?.payload?.sub;
        return typeof sub === "string" && sub ? sub : null;
    } catch {
        return null;
    }
}

// ─── catalog ───────────────────────────────────────────────────────────────

/**
 * The catalog, or an "unavailable" failure.
 *
 * `failure.unavailable` distinguishes "this environment has no integrations"
 * from "this user has none connected" — the two look identical on the wire
 * (404 / 503) and must not look identical on screen.
 */
export function useCatalog(options: Query<Catalog> = {}) {
    const query = useQuery({
        queryKey: integrationsKeys.catalog(),
        queryFn: fetchCatalog,
        enabled: INTEGRATIONS_ENABLED && options.enabled !== false,
        staleTime: 30_000,
        retry: retryServerErrorsOnly,
        ...options,
    });

    const failure: IntegrationFailure | null = query.error ? describeFailure(query.error) : null;

    return {
        ...query,
        failure,
        /** True when the product is not available here at all. */
        unavailable: !INTEGRATIONS_ENABLED || !!failure?.unavailable || query.data?.enabled === false,
        /** The gate itself, so a caller can tell it apart from a failed request. */
        gated: !INTEGRATIONS_ENABLED,
    };
}

/*
 * There is no hook for `GET /integrations/{appId}`, on purpose.
 *
 * Backend's answer: that route returns the same `CatalogApp` selected from
 * `GET /integrations` — "it won't expose additional fields or capabilities or
 * anything" — and the frontend does not need to call it. So a card is rendered
 * from the catalog, and the catalog is refetched after connect, disconnect and
 * callback completion. Reaching for the single-app route to get *more* about an
 * app would return exactly the same object and one more request.
 */

// ─── connection ────────────────────────────────────────────────────────────

/**
 * Begin a connection.
 *
 * Stores the attempt before handing the URL back, so the caller can open it
 * inside the user's gesture without racing the write. The `redirect_url` is
 * returned but never persisted.
 */
export function useStartConnect() {
    return useMutation({
        mutationFn: async (appId: AppId) => {
            /* Resolved here rather than read from hook state: a click that
               lands before the session lookup settles would otherwise store
               the attempt against no user at all, and the callback could never
               match it. No user, no attempt. */
            const userKey = await resolveMerkoUserKey();
            if (!userKey) {
                throw new IntegrationClientError(
                    "no_session",
                    "Your session could not be confirmed. Sign in again, then connect.",
                );
            }

            const attempt = await startConnect(appId);
            saveAttempt({
                attemptId: attempt.attempt_id,
                appId,
                expiresAt: attempt.expires_at,
                userKey,
            });
            return attempt;
        },
        retry: false,
    });
}

/** Poll a stored attempt. Not proof of a live connection — see the handoff §3. */
export function useAttempt(attemptId: string | null, options: { poll?: boolean } = {}) {
    return useQuery({
        queryKey: integrationsKeys.attempt(attemptId ?? ""),
        queryFn: () => fetchAttempt(attemptId as string),
        enabled: INTEGRATIONS_ENABLED && !!attemptId,
        retry: retryServerErrorsOnly,
        refetchInterval: options.poll ? ACTION_POLL.initialMs : false,
    });
}

/**
 * Complete an attempt.
 *
 * `sessionUri` is passed for the OAuth hand-off and omitted for an API-key link
 * — and omitted again for any retry after a failure that may already have
 * redeemed it, because the URI is single-use.
 */
export function useCompleteAttempt() {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: ({ attemptId, sessionUri }: { attemptId: string; sessionUri?: string }) =>
            completeAttempt(attemptId, sessionUri),
        retry: false,
        onSuccess: (result) => {
            // Only `active` ends the flow; `pending` keeps the attempt alive so
            // the caller can poll before claiming anything.
            if (result.status === "active") clearAttempt();
            void qc.invalidateQueries({ queryKey: integrationsKeys.catalog() });
        },
    });
}

/**
 * Disconnect an app.
 *
 * Also drops the cached profile list, since the backend disables this app's
 * import profiles as part of the same operation.
 */
export function useDisconnectApp() {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: (appId: AppId) => disconnectApp(appId),
        retry: false,
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: integrationsKeys.catalog() });
            void qc.invalidateQueries({ queryKey: integrationsKeys.profiles() });
        },
    });
}

// ─── sources ───────────────────────────────────────────────────────────────

/** Discovery for a connected app. Meaningless before connection. */
export function useSources(appId: AppId | null, options: { enabled?: boolean } = {}) {
    return useQuery<Sources>({
        queryKey: integrationsKeys.sources((appId ?? "mailchimp") as AppId),
        queryFn: () => fetchSources(appId as AppId),
        enabled: INTEGRATIONS_ENABLED && !!appId && options.enabled !== false,
        staleTime: 60_000,
        retry: retryServerErrorsOnly,
    });
}

// ─── profiles ──────────────────────────────────────────────────────────────

export function useProfiles(options: Query<Profile[]> = {}) {
    return useQuery({
        queryKey: integrationsKeys.profiles(),
        queryFn: fetchProfiles,
        enabled: INTEGRATIONS_ENABLED && options.enabled !== false,
        staleTime: 15_000,
        retry: retryServerErrorsOnly,
        ...options,
    });
}

export function useCreateProfile() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: ProfileInput) => createProfile(input),
        retry: false,
        onSuccess: () => void qc.invalidateQueries({ queryKey: integrationsKeys.profiles() }),
    });
}

/** PATCH is a full replacement; pass a clean `ProfileInput`, not a `Profile`. */
export function useUpdateProfile() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ profileId, input }: { profileId: string; input: ProfileInput }) =>
            updateProfile(profileId, input),
        retry: false,
        onSuccess: () => void qc.invalidateQueries({ queryKey: integrationsKeys.profiles() }),
    });
}

export function useDeleteProfile() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (profileId: string) => deleteProfile(profileId),
        retry: false,
        onSuccess: () => void qc.invalidateQueries({ queryKey: integrationsKeys.profiles() }),
    });
}

/** A ten-row sample at most — never a count, never an eligibility guarantee. */
export function usePreviewProfile() {
    return useMutation<Preview, any, string>({
        mutationFn: (profileId: string) => previewProfile(profileId),
        retry: false,
    });
}

// ─── runs and jobs ─────────────────────────────────────────────────────────

/**
 * True once the server has actually answered a request.
 *
 * The distinction the handoff's key rules hang on. A response — success or a
 * considered error — closes the request, so its key is spent. *No* response
 * (a dropped connection, a timeout) leaves it open: the run may have been
 * accepted, and only re-sending the **same** key lets the server recognise the
 * duplicate instead of starting a second import.
 */
const serverAnswered = (error: any): boolean => !!error?.response;

/**
 * Sync now.
 *
 * The key is minted per profile and released only once the server answers, so
 * a genuine second click gets a new key while a retry after a lost response
 * reuses the one still in flight.
 */
export function useStartRun() {
    const qc = useQueryClient();

    return useMutation<ImportJob, any, string>({
        mutationFn: (profileId: string) => startRun(profileId, idempotencyKeyFor(profileId)),
        retry: false,
        onSuccess: (_job, profileId) => releaseIdempotencyKey(profileId),
        onError: (error, profileId) => {
            if (serverAnswered(error)) releaseIdempotencyKey(profileId);
        },
        onSettled: () => void qc.invalidateQueries({ queryKey: integrationsKeys.profiles() }),
    });
}

/**
 * A terminal-job retry: a **new** key, and a new full scan.
 *
 * Keyed by the job being retried rather than by its profile, so the key can
 * never be the one the original run used, and a lost response to the retry
 * itself still re-sends the same key.
 */
export function useRetryJob() {
    const qc = useQueryClient();
    const slot = (jobId: string) => `retry:${jobId}`;

    return useMutation<ImportJob, any, { jobId: string; profileId: string }>({
        mutationFn: ({ jobId }) => retryJob(jobId, idempotencyKeyFor(slot(jobId))),
        retry: false,
        onSuccess: (_job, { jobId }) => releaseIdempotencyKey(slot(jobId)),
        onError: (error, { jobId }) => {
            if (serverAnswered(error)) releaseIdempotencyKey(slot(jobId));
        },
        onSettled: () => void qc.invalidateQueries({ queryKey: integrationsKeys.profiles() }),
    });
}

/**
 * Poll a job until it settles. REST is the source of truth, whatever the
 * WebSocket projection says.
 */
export function useJob(jobId: string | null) {
    const polls = useRef(0);

    const query = useQuery({
        queryKey: integrationsKeys.job(jobId ?? ""),
        queryFn: () => fetchJob(jobId as string),
        enabled: INTEGRATIONS_ENABLED && !!jobId,
        retry: retryServerErrorsOnly,
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            if (!status) return JOB_POLL.initialMs;
            if (isTerminalJob(status)) return false;
            polls.current += 1;
            return pollInterval(polls.current, JOB_POLL);
        },
    });

    useEffect(() => {
        polls.current = 0;
    }, [jobId]);

    const terminal = !!query.data && isTerminalJob(query.data.status);
    return { ...query, terminal };
}

/**
 * The WebSocket projection for a job, as a hint only.
 *
 * Gives the UI something to move between REST polls, and nudges the poll when a
 * frame says the job settled. It never sets the job's status itself: the
 * projection can lag or be absent, so REST stays authoritative.
 */
export function useJobProgressHint(jobId: string | null): IntegrationJobProgress | null {
    const qc = useQueryClient();
    const [hint, setHint] = useState<IntegrationJobProgress | null>(() =>
        jobId ? lastJobProgress(jobId) ?? null : null,
    );

    useEffect(() => {
        setHint(jobId ? lastJobProgress(jobId) ?? null : null);
        if (!jobId) return;

        return subscribeJobProgress((progress) => {
            if (progress.jobId !== jobId) return;
            setHint(progress);
            // A frame claiming the job finished is worth confirming at once
            // rather than at the next poll tick.
            if (progress.percent === 100 || progress.status === "completed" ||
                progress.status === "completed_with_errors" || progress.status === "failed") {
                void qc.invalidateQueries({ queryKey: integrationsKeys.job(jobId) });
            }
        });
    }, [jobId, qc]);

    return hint;
}

// ─── external writes ───────────────────────────────────────────────────────

export function usePreviewAction() {
    return useMutation<
        ExternalAction,
        any,
        { appId: AppId; operation: ActionOperation; fields: Record<string, string> }
    >({
        mutationFn: ({ appId, operation, fields }) => previewAction(appId, operation, fields),
        retry: false,
    });
}

/** Confirm sends only the hash from the preview; it is never recomputed. */
export function useConfirmAction() {
    const qc = useQueryClient();

    return useMutation<ExternalAction, any, { actionId: string; previewHash: string }>({
        mutationFn: ({ actionId, previewHash }) => confirmAction(actionId, previewHash),
        retry: false,
        onSuccess: (action) =>
            void qc.setQueryData(integrationsKeys.action(action.id), action),
    });
}

export function useAction(actionId: string | null) {
    const polls = useRef(0);

    const query = useQuery({
        queryKey: integrationsKeys.action(actionId ?? ""),
        queryFn: () => fetchAction(actionId as string),
        enabled: INTEGRATIONS_ENABLED && !!actionId,
        retry: retryServerErrorsOnly,
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            if (!status) return ACTION_POLL.initialMs;
            if (isTerminalAction(status)) return false;
            polls.current += 1;
            return pollInterval(polls.current, ACTION_POLL);
        },
    });

    useEffect(() => {
        polls.current = 0;
    }, [actionId]);

    return query;
}

// ─── shared helpers ────────────────────────────────────────────────────────

/** Turn whatever a mutation threw into the one failure shape the UI renders. */
export function useFailure(error: unknown): IntegrationFailure | null {
    return useMemo(() => (error ? describeFailure(error) : null), [error]);
}

/** Refresh the catalog — the handoff's answer to most connection error codes. */
export function useRefreshCatalog() {
    const qc = useQueryClient();
    return useCallback(() => {
        void qc.invalidateQueries({ queryKey: integrationsKeys.catalog() });
    }, [qc]);
}
